import {
  LeasePaymentFrequency,
  LeasePaymentInvoicePeriod,
  LeaseVariousOperationBillingMode,
  LeaseVariousOperationPeriodNature,
  VariousOperationType,
} from '../types';

import { willBeForcedToSeparateInvoice } from './leaseVariousOperationFrequencyUtils';

export type SeparateDocumentReason = 'OWN_PERIOD' | 'FREQUENCY_MISMATCH';

export interface LeaseBillingContext {
  paymentFrequency: LeasePaymentFrequency;
  paymentInvoicePeriod: LeasePaymentInvoicePeriod;
  paymentInvoicePeriodCustomDay?: number | null;
}

export interface BillingItemFrequencyInput {
  type: VariousOperationType;
  paymentFrequency: LeasePaymentFrequency;
}

export interface BillingItemNatureInput extends BillingItemFrequencyInput {
  paymentInvoicePeriod: LeasePaymentInvoicePeriod;
  paymentInvoicePeriodCustomDay?: number | null;
}

export interface BillingItemModeInput extends BillingItemFrequencyInput {
  separateInvoice: boolean;
}

/** A one-time row can carry a stale `paymentFrequency`; `type` decides, not the frequency. */
export const getEffectiveFrequency = (
  operation: BillingItemFrequencyInput,
  leaseFrequency: LeasePaymentFrequency
): LeasePaymentFrequency | 'NONE' =>
  operation.type === VariousOperationType.ONE_TIME ? 'NONE' : (operation.paymentFrequency ?? leaseFrequency);

/** A stale custom day on a non-custom period must not defeat an otherwise-matching anchor. */
const sharesRentPeriodAnchor = (operation: BillingItemNatureInput, lease: LeaseBillingContext): boolean => {
  if (operation.paymentInvoicePeriod !== lease.paymentInvoicePeriod) {
    return false;
  }
  if (operation.paymentInvoicePeriod !== LeasePaymentInvoicePeriod.CUSTOM_PERIOD) {
    return true;
  }
  return (operation.paymentInvoicePeriodCustomDay ?? null) === (lease.paymentInvoicePeriodCustomDay ?? null);
};

/**
 * Display only — never gate a billing decision on this. The nightly run ignores the period anchor
 * while `periodNature` is unstored, so a derived `OWN_PERIOD` would contradict what is billed.
 * `EVENT` is never derived. See docs/specs/lvo-schedule/01-engine.md §4.
 */
export const derivePeriodNature = (
  operation: BillingItemNatureInput,
  lease: LeaseBillingContext
): LeaseVariousOperationPeriodNature => {
  const effectiveFrequency = getEffectiveFrequency(operation, lease.paymentFrequency);
  const shareableFrequency = effectiveFrequency === 'NONE' || effectiveFrequency === lease.paymentFrequency;

  return shareableFrequency && sharesRentPeriodAnchor(operation, lease)
    ? LeaseVariousOperationPeriodNature.RENT_ALIGNED
    : LeaseVariousOperationPeriodNature.OWN_PERIOD;
};

/**
 * With no explicit `periodNature` this is exactly today's rule, so the dialog and the nightly run
 * cannot disagree. Reads `paymentFrequency` regardless of `type`, and ignores `separateInvoice` (a
 * user's choice is not a forcing) — both deliberate, see docs/specs/lvo-schedule/01-engine.md §4.
 */
export const isSeparateDocumentForced = (
  operation: BillingItemFrequencyInput & { periodNature?: LeaseVariousOperationPeriodNature },
  lease: LeaseBillingContext
): boolean => {
  if (operation.periodNature === LeaseVariousOperationPeriodNature.OWN_PERIOD) {
    return true;
  }
  return willBeForcedToSeparateInvoice(operation.paymentFrequency ?? lease.paymentFrequency, lease.paymentFrequency);
};

/** A user-chosen `separateInvoice` surfaces no reason: the hint is only for a choice he cannot make. */
export const getSeparateDocumentReason = (
  operation: BillingItemFrequencyInput & { periodNature?: LeaseVariousOperationPeriodNature },
  lease: LeaseBillingContext
): SeparateDocumentReason | null => {
  if (!isSeparateDocumentForced(operation, lease)) {
    return null;
  }
  return operation.periodNature === LeaseVariousOperationPeriodNature.OWN_PERIOD ? 'OWN_PERIOD' : 'FREQUENCY_MISMATCH';
};

/** `IMMEDIATE` is never derived — it is an encoding-time event, not a stored state. */
export const getEffectiveBillingMode = (
  operation: BillingItemModeInput,
  lease: LeaseBillingContext
): LeaseVariousOperationBillingMode =>
  operation.separateInvoice || isSeparateDocumentForced(operation, lease)
    ? LeaseVariousOperationBillingMode.OWN_SCHEDULE
    : LeaseVariousOperationBillingMode.WITH_RENT;

/**
 * Consults the period anchor only when `periodNature` is supplied explicitly: while it is unstored
 * the nightly run decides on frequency alone, so reading a legacy anchor mismatch here would make
 * the preview promise a separate document for a line it posts onto the rent invoice.
 */
export const isRentAligned = (
  operation: BillingItemModeInput & BillingItemNatureInput & { periodNature?: LeaseVariousOperationPeriodNature },
  lease: LeaseBillingContext
): boolean => {
  if (getEffectiveBillingMode(operation, lease) !== LeaseVariousOperationBillingMode.WITH_RENT) {
    return false;
  }
  if (operation.periodNature === undefined) {
    return true;
  }
  return operation.periodNature === LeaseVariousOperationPeriodNature.RENT_ALIGNED;
};

export interface StoredBillingFields {
  billingMode?: LeaseVariousOperationBillingMode | null;
  periodNature?: LeaseVariousOperationPeriodNature | null;
}

export const resolveBillingMode = (
  operation: BillingItemModeInput & StoredBillingFields,
  lease: LeaseBillingContext
): LeaseVariousOperationBillingMode => operation.billingMode ?? getEffectiveBillingMode(operation, lease);

/**
 * A legacy rent-riding row is RENT_ALIGNED whatever its stored anchor says: the nightly run bills it on
 * the rent period, and answering OWN_PERIOD would force it onto its own document on the next read.
 */
export const resolvePeriodNature = (
  operation: BillingItemModeInput & BillingItemNatureInput & StoredBillingFields,
  lease: LeaseBillingContext
): LeaseVariousOperationPeriodNature => {
  const derivedNature =
    resolveBillingMode(operation, lease) === LeaseVariousOperationBillingMode.WITH_RENT
      ? LeaseVariousOperationPeriodNature.RENT_ALIGNED
      : derivePeriodNature(operation, lease);
  return operation.periodNature ?? derivedNature;
};

export const isOwnDocument = (
  operation: BillingItemModeInput & StoredBillingFields,
  lease: LeaseBillingContext
): boolean => resolveBillingMode(operation, lease) !== LeaseVariousOperationBillingMode.WITH_RENT;

/**
 * The raw flag, stored-first — for the sites that decide whether a charge line folds into the rent's
 * charges rather than whether it gets its own document. A charge forced separate by frequency alone
 * folds into the rent today; reading `isOwnDocument` there would bill it twice.
 */
export const resolveSeparateInvoice = (
  operation: Pick<BillingItemModeInput, 'separateInvoice'> & Pick<StoredBillingFields, 'billingMode'>
): boolean => {
  const storedMode = operation.billingMode;
  return storedMode ? storedMode !== LeaseVariousOperationBillingMode.WITH_RENT : operation.separateInvoice;
};
